import React from "react";
import { ErrorMessage, Field, Form, Formik, FormikHelpers } from "formik";
import { object, string } from "yup";
import cc from "classcat";

import { ConfirmationFormValues } from "../types";
import { PrimaryButton } from "./primary-button";
import { useContainerSize } from "../hooks";
import { useOptInStore } from "../store";

interface ConfirmationFormikProps {
  error?: string;
  onSubmit: (
    values: ConfirmationFormValues,
    formikHelpers: FormikHelpers<ConfirmationFormValues>
  ) => Promise<void>;
}

const initialValues: ConfirmationFormValues = {
  code: "",
  phone_number: "",
};

const confirmationSchema = object().shape({
  code: string()
    .required("Please enter a valid 6-digit verification code")
    .length(6, "Verification code must be 6 digits."),
});

export function ConfirmationFormik(props: ConfirmationFormikProps) {
  const size = useContainerSize();
  const phoneNumber = useOptInStore((state) => state.phoneNumber) || "";
  const onSubmit = (
    values: ConfirmationFormValues,
    helpers: FormikHelpers<ConfirmationFormValues>
  ) => props.onSubmit({ ...values, phone_number: phoneNumber }, helpers);

  return (
    <Formik
      initialErrors={{ code: props.error }}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={confirmationSchema}
      enableReinitialize
    >
      {({ isSubmitting }) => (
        <Form>
          <div data-field className="space-y-2">
            <div
              className={cc([
                {
                  "flex space-x-2": size === "desktop",
                  "space-y-2": size === "mobile",
                },
              ])}
            >
              <div className="flex-1">
                <div className="relative">
                  <label className="sr-only" htmlFor="code">
                    Verification code
                  </label>
                  <Field
                    id="code"
                    className="w-full border-radius-primary border-[#C9CCCF] focus-primary focus-primary-border placeholder-subdued text-body"
                    placeholder="Enter 6-digit verification code"
                    name="code"
                    maxLength={6}
                    type="text"
                  />
                </div>
                <ErrorMessage
                  className="caption text-critical mt-2"
                  component="p"
                  name="code"
                />
              </div>
              <PrimaryButton
                className={cc([
                  {
                    "h-full": size === "desktop",
                    "w-full justify-center": size === "mobile",
                  },
                ])}
                loading={isSubmitting}
                type="submit"
              >
                {isSubmitting ? "Confirming..." : "Confirm"}
              </PrimaryButton>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
